import type { TranslationKey } from '../tr';

/** English dictionary. The `Record<TranslationKey, string>` type guarantees parity with `tr`. */
export const en: Record<TranslationKey, string> = {
  // ---- Application ------------------------------------------------------
  'app.name': 'PLC Simulator',
  'app.tagline': 'Education and process simulation',
  'app.untitledProject': 'Untitled',

  // ---- Toolbar ----------------------------------------------------------
  'toolbar.label': 'Toolbar',
  'toolbar.group.file': 'File actions',
  'toolbar.group.edit': 'Edit actions',
  'toolbar.group.modes': 'Working modes',
  'toolbar.group.run': 'Run controls',
  'toolbar.new': 'New',
  'toolbar.open': 'Open',
  'toolbar.save': 'Save',
  'toolbar.undo': 'Undo',
  'toolbar.redo': 'Redo',
  'toolbar.run': 'Run',
  'toolbar.pause': 'Pause',
  'toolbar.step': 'Step',
  'toolbar.reset': 'Reset',
  'toolbar.stop': 'Stop',
  'toolbar.theme.light': 'Light Theme',
  'toolbar.theme.dark': 'Dark Theme',
  'toolbar.settings': 'Settings',
  'toolbar.help': 'Help',
  'toolbar.unavailable': 'This feature will be available in a later development phase.',
  'toolbar.confirmNew': 'There are unsaved changes. Do you want to start a new project?',
  'toolbar.confirmOpen': 'There are unsaved changes. Do you want to open another project?',

  // ---- Modes ------------------------------------------------------------
  'mode.design': 'Design',
  'mode.program': 'Program',
  'mode.workshop': 'Workshop',
  'mode.hmi': 'HMI',
  'mode.simulation': 'Simulation',
  'mode.monitor': 'Monitor',
  'mode.debug': 'Debug',
  'mode.design.description': 'Design the scene and devices',
  'mode.program.description': 'Write the ladder program',
  'mode.workshop.description': 'Electrical control and schematic workshop',
  'mode.hmi.description': 'Design Operator (HMI) screens',
  'mode.simulation.description': 'Run and observe the process',
  'mode.monitor.description': 'Watch PLC variables',
  'mode.debug.description': 'Inspect the program step by step',

  // ---- Workspace --------------------------------------------------------
  'workspace.label': 'Workspace',
  'workspace.design.title': 'Scene Builder',
  'workspace.program.placeholder':
    'The ladder editor will be available in a later development phase.',
  'workspace.simulation.placeholder':
    'The simulation engine will be available in a later development phase.',
  'workspace.monitor.placeholder':
    'Monitoring tools will be available in a later development phase.',
  'workspace.debug.placeholder': 'Debugging tools will be available in a later development phase.',
  'workspace.placeholder.badge': 'Coming soon',
  'scene.label': 'Scene area',
  'scene.empty.title': 'Empty scene',
  'scene.empty.description': 'Add a component from the toolbox.',
  'scene.zoom.in': 'Zoom In',
  'scene.zoom.out': 'Zoom Out',
  'scene.zoom.reset': 'Reset View (100%)',
  'scene.zoom.fit': 'Fit / Center View',

  // ---- Components panel -------------------------------------------------
  'components.title': 'Components',
  'components.label': 'Component toolbox',
  'components.soon': 'Soon',
  'components.category.input': 'Input',
  'components.category.output': 'Output',
  'components.category.mechanical': 'Mechanical',
  'components.category.process': 'Process',
  'components.category.network': 'Network',
  'components.empty.title': 'No components',
  'components.empty.description': 'No registered component was found.',

  // ---- Device types -----------------------------------------------------
  'device.button.name': 'Button',
  'device.button.description': 'A push button that changes a PLC input when pressed.',
  'device.switch.name': 'Switch',
  'device.switch.description': 'A selector or toggle switch that keeps its position.',
  'device.emergency-stop.name': 'Emergency Stop',
  'device.emergency-stop.description': 'A safety button that latches when pressed.',
  'device.sensor.name': 'Sensor',
  'device.sensor.description': 'A proximity or photoelectric sensor that detects objects.',
  'device.lamp.name': 'Lamp',
  'device.lamp.description': 'An indicator lamp driven by a PLC output.',
  'device.motor.name': 'Motor',
  'device.motor.description': 'An electric motor driven by a PLC output.',
  'device.buzzer.name': 'Buzzer',
  'device.buzzer.description': 'A warning device that sounds from a PLC output.',
  'device.fan.name': 'Cooling Fan',
  'device.fan.description': 'AC cooling fan driven by a PLC output.',
  'device.conveyor.name': 'Conveyor',
  'device.conveyor.description': 'A belt system that carries products.',
  'device.cylinder.name': 'Cylinder',
  'device.cylinder.description': 'A pneumatic cylinder with back-and-forth motion.',
  'device.valve.name': 'Solenoid Valve',
  'device.valve.description': 'Electromagnetic coil operated industrial flow on/off valve.',
  'device.tank.name': 'Tank',
  'device.tank.description': 'A liquid storage tank with a monitored level.',
  'device.pump.name': 'Pump',
  'device.pump.description': 'A pump that moves liquid.',
  'device.mixer.name': 'Mixer Motor',
  'device.mixer.description': 'Vertical agitator motor with shaft and impeller mounted on tank.',
  'device.product.name': 'Product',
  'device.product.description': 'A box or product carried on a conveyor.',
  'device.jam-sensor.name': 'Jam Sensor',
  'device.jam-sensor.description': 'Detects product jams on a conveyor.',
  'device.level-sensor.name': 'Level Sensor',
  'device.level-sensor.description': 'Measures liquid level in a tank and outputs an analog signal (0-10V / 4-20mA).',
  'device.pid-controller.name': 'PID Controller',
  'device.pid-controller.description': 'Process PID controller regulating process variable (PV) to setpoint (SP).',
  'device.modbus-server.name': 'Modbus TCP Server',
  'device.modbus-server.description': 'Simulated Modbus TCP Slave/Server for PLC and SCADA communication.',
  'device.modbus-client.name': 'Modbus TCP Client',
  'device.modbus-client.description': 'Modbus TCP Master/Client for reading and writing data to remote servers.',

  // ---- Properties panel -------------------------------------------------
  'properties.title': 'Properties',
  'properties.label': 'Properties panel',
  
  // Device Properties
  'properties.color': 'Color',
  'properties.variant': 'Variant / Type',
  'device.motor.standard': '3-Phase AC Motor',
  'device.motor.pumpMotor': 'Motor-Pump Unit',
  'device.motor.fan': 'Cooling Fan (AC Single)',
  'variant.standard': 'Standard AC Motor',
  'variant.pump_motor': 'Pump Motor',
  'variant.fan': 'Cooling Fan',
  'variant.mixer': 'Vertical Mixer Motor',
  'sensor.variant.photoelectric': 'Photoelectric Beam Sensor',
  'sensor.variant.inductive': 'Inductive Proximity Sensor (Metal)',
  'sensor.variant.capacitive': 'Capacitive Sensor (Universal)',
  'sensor.variant.light_curtain': 'Optical Light Curtain',
  'properties.sensingRange': 'Sensing Range (px)',
  'properties.logicType': 'Contact Logic',
  'logic.no': 'Normally Open (NO)',
  'logic.nc': 'Normally Closed (NC)',
  'properties.buttonLabel': 'Label',
  'properties.motorId': 'Motor ID',
  'properties.length': 'Length',
  'properties.width': 'Width',
  'properties.speed': 'Speed (px/s)',
  'properties.direction': 'Direction',
  'direction.right': 'Right (Forward)',
  'direction.left': 'Left (Reverse)',
  'properties.endAction': 'End of Belt Action',
  'endAction.dropOff': 'Drop Off (Collection Bin)',
  'endAction.stopper': 'Mechanical Stopper (Jam Alarm)',
  'properties.minSpacing': 'Min Spacing',
  'properties.jamThreshold': 'Jam Threshold (ms)',
  'properties.outputConveyor': 'Output Conveyor',
  'properties.conveyorId': 'Conveyor ID',
  'properties.capacity': 'Capacity (Liters)',
  'properties.initialLevel': 'Initial Level (L)',
  'properties.inletCount': 'Inlet Pipe Count',
  'properties.inlet1': '1 Inlet (Top)',
  'properties.inlet2': '2 Inlets (Left)',
  'properties.inlet3': '3 Inlets (Left)',
  'properties.inlet4': '4 Inlets (Left)',
  'properties.tankId': 'Mounted Tank ID',
  'properties.bladeType': 'Impeller Type',
  'properties.rpm': 'Rotation Speed (RPM)',
  'blade.propeller': 'Marine Propeller',
  'blade.turbine': 'Turbine Impeller',
  'blade.anchor': 'Anchor Agitator',
  'properties.voltage': 'Coil Voltage',
  'properties.valveType': 'Valve Type',
  'valve.nc': 'Normally Closed (NC)',
  'valve.no': 'Normally Open (NO)',
  'voltage.24vdc': '24V DC',
  'voltage.230vac': '230V AC',
  'voltage.12vdc': '12V DC',
  'properties.nominalFlowRate': 'Nominal Flow Rate (L/s)',
  'properties.maxFlowRate': 'Max Flow Rate (L/s)',
  'properties.sourceId': 'Source ID',
  'properties.targetId': 'Target ID',
  'properties.minLevel': 'Min Level (L)',
  'properties.maxLevel': 'Max Level (L)',
  'properties.signalType': 'Signal Type',
  'signal.0_10V': '0 - 10 V (Analog Voltage)',
  'signal.4_20mA': '4 - 20 mA (Analog Current)',
  'properties.pvSource': 'PV Source (Sensor ID)',
  'properties.cvTarget': 'CV Target (Actuator ID)',
  'properties.controlMode': 'Control Mode',
  'properties.workMode': 'Operating Mode',
  'properties.setpoint': 'Setpoint (SP)',
  'properties.manualCv': 'Manual CV (%)',
  'properties.kp': 'Proportional Gain (Kp)',
  'properties.ki': 'Integral Gain (Ki)',
  'properties.kd': 'Derivative Gain (Kd)',
  'properties.sampleTimeMs': 'Sample Time (ms)',
  'properties.minOutput': 'Min Output (%)',
  'properties.maxOutput': 'Max Output (%)',
  'P': 'P (Proportional)',
  'PI': 'PI (Proportional-Integral)',
  'PID': 'PID (Proportional-Integral-Derivative)',
  'AUTO': 'Automatic (AUTO)',
  'MANUAL': 'Manual (MANUAL)',
  'properties.unitId': 'Unit ID',
  'properties.isOnline': 'Online',
  'properties.serverId': 'Server ID',
  'properties.functionCode': 'Function Code (FC)',
  'properties.startAddress': 'Start Address',
  'properties.quantity': 'Quantity',
  'properties.writeValues': 'Write Values',
  'properties.trigger': 'Trigger Variable',
  'properties.delayMs': 'Polling Delay (ms)',
  
  // Colors
  'color.green': 'Green',
  'color.red': 'Red',
  'color.yellow': 'Yellow',
  'color.blue': 'Blue',
  'color.industrialBlue': 'Industrial Blue',
  'color.cyan': 'Cyan / Teal',
  'color.gray': 'Gray / Anthracite',
  'color.orange': 'Orange',
  'color.white': 'White',
  'properties.empty.title': 'No object selected',
  'properties.empty.description': 'Select an object to see its properties.',
  'properties.noSchema': 'This object has no editable properties defined.',
  'properties.general': 'General',
  'properties.field.id': 'ID',
  'properties.field.type': 'Type',
  'properties.field.name': 'Name / Tag',
  'properties.showNameLabel': 'Show Name Tag',
  'properties.ladder.address': 'PLC Address / Tag',
  'properties.ladder.presetTime': 'Preset Time (PT)',
  'properties.ladder.presetValue': 'Preset Value (PV)',
  'properties.ladder.in1': 'Input 1 (IN1)',
  'properties.ladder.in2': 'Input 2 (IN2)',
  'properties.ladder.in': 'Input (IN)',
  'properties.ladder.out': 'Output Address (OUT)',
  'properties.ladder.opType': 'Operator / Element Type',
  'properties.ladder.label': 'Label',
  'properties.ladder.min': 'Lower Limit (MIN)',
  'properties.ladder.max': 'Upper Limit (MAX)',
  'properties.ladder.inMin': 'Input Min (IN_MIN)',
  'properties.ladder.inMax': 'Input Max (IN_MAX)',
  'properties.ladder.outMin': 'Output Min (OUT_MIN)',
  'properties.ladder.outMax': 'Output Max (OUT_MAX)',
  'properties.type.network': 'Network',
  'properties.type.wire': 'Wire',

  // ---- Bottom panel -----------------------------------------------------
  'bottom.label': 'Bottom panel',
  'bottom.tabs.label': 'Bottom panel tabs',
  'bottom.tab.ladder': 'Ladder',
  'bottom.tab.variables': 'Variables',
  'bottom.tab.plcMonitor': 'PLC Monitor',
  'bottom.tab.alarms': 'Alarms',
  'bottom.tab.events': 'Events',
  'bottom.tab.diagnostics': 'Diagnostics',
  'bottom.tab.modbus': 'Modbus Monitor',
  'bottom.tab.console': 'Console',
  'bottom.placeholder.ladder': 'The ladder program will be shown here.',
  'bottom.placeholder.variables': 'The tag and variable table will be shown here.',
  'bottom.placeholder.plcMonitor': 'PLC input/output states will be shown here.',
  'bottom.placeholder.events': 'Simulation events will be listed here.',
  'bottom.placeholder.diagnostics': 'Diagnostic information will be shown here.',
  'bottom.placeholder.modbus': 'Modbus communication states will be shown here.',
  'bottom.modbus.noServers': 'No Modbus Servers available in the project.',
  'bottom.placeholder.console': 'System messages will be shown here.',
  'bottom.placeholder.title': 'No content yet',

  // ---- Status bar -------------------------------------------------------
  'status.label': 'Status bar',
  'status.ready': 'Ready',
  'status.cpu': 'CPU',
  'status.fps': 'FPS',
  'status.plc': 'PLC',
  'status.plc.notRunning': 'Not running',
  'status.simulation': 'Simulation',
  'status.simulation.stopped': 'Stopped',
  'status.simulation.running': 'Running',
  'status.simulation.paused': 'Paused',
  'status.project': 'Project',
  'status.panels.label': 'Panel visibility',
  'status.panel.left': 'Show/hide components panel',
  'status.panel.right': 'Show/hide properties panel',
  'status.panel.bottom': 'Show/hide bottom panel',
  'status.panel.developer': 'Show/hide developer panel',
  'status.unavailable': '--',

  // ---- Developer panel --------------------------------------------------
  'developer.title': 'Developer',
  'developer.label': 'Developer panel',
  'developer.fps': 'FPS',
  'developer.frameTime': 'Frame time',
  'developer.plcScan': 'PLC scan',
  'developer.simulationTime': 'Simulation time',
  'developer.simulationSpeed': 'Simulation speed',
  'developer.activeDevices': 'Active devices',
  'developer.eventQueue': 'Event queue',
  'developer.renderObjects': 'Render objects',
  'developer.uiSnapshotRate': 'UI snapshot rate',

  // ---- Settings ---------------------------------------------------------
  'settings.title': 'Settings',
  'settings.close': 'Close',
  'settings.language': 'Language',
  'settings.language.tr': 'Türkçe',
  'settings.language.en': 'English',
  'settings.theme': 'Theme',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.developer': 'Show developer panel',
  'settings.shortcuts': 'Keyboard shortcuts',

  // ---- Shortcuts --------------------------------------------------------
  'shortcut.save': 'Save',
  'shortcut.undo': 'Undo',
  'shortcut.redo': 'Redo',
  'shortcut.delete': 'Delete selected object',
  'shortcut.escape': 'Clear selection / close dialog',
  'shortcut.toggleDeveloper': 'Toggle developer panel',

  // ---- Notifications ----------------------------------------------------
  'toast.featureUnavailable': 'This feature is not available yet.',
  'toast.nothingSelected': 'Nothing is selected.',
  'toast.dismiss': 'Dismiss notification',
  'toast.region': 'Notifications',
  'toast.compileSuccess': 'Compilation successful.',
  'toast.compileFailed': 'Compilation failed. Please check errors.',
  'toast.projectOpened': 'Project opened successfully.',
  'toast.projectOpenError': 'Failed to open project file.',
  'toast.projectSaved': 'Project saved successfully.',
  'toast.projectSaveError': 'Failed to save project.',
  'toast.undoRedoWip': 'Undo/Redo feature is currently under development.',

  // ---- HMI Properties ----------------------------------------------------
  'hmi.prop.x': 'X Position',
  'hmi.prop.y': 'Y Position',
  'hmi.prop.width': 'Width',
  'hmi.prop.height': 'Height',
  'hmi.prop.label': 'Label',
  'hmi.prop.color': 'Color',
  'hmi.prop.behavior': 'Behavior',
  'hmi.prop.momentary': 'Momentary (Push)',
  'hmi.prop.toggle': 'Toggle (Switch)',
  'hmi.prop.bindingType': 'Binding Type',
  'hmi.prop.none': 'None',
  'hmi.prop.plc': 'PLC Tag / Address',
  'hmi.prop.modbus': 'Modbus Register',
  'hmi.prop.alarm': 'Alarm System',
  'hmi.prop.address': 'Address / Tag',
  'hmi.prop.scaleMin': 'Scale Min',
  'hmi.prop.scaleMax': 'Scale Max',

  // ---- Error boundary ---------------------------------------------------
  'error.title': 'Something went wrong.',
  'error.description': 'The application encountered an unexpected situation.',
  'error.retry': 'Try again',
  'error.details': 'Technical details',

  // ---- Common -----------------------------------------------------------
  'common.loading': 'Loading…',

  // ---- Ladder Editor Drawing Mode ---------------------------------------
  'ladder.drawingMode': 'Drawing Mode',
  'ladder.drawingMode.hLine': 'Horizontal Line (H_LINE)',
  'ladder.drawingMode.hLineHint': 'Drag across cells to draw lines in series. Press Esc or right-click to exit.',
  'ladder.drawingMode.elementHint': 'Click on a cell to place. Press Esc or right-click to exit.',
};
